import { type Card, DECK, RANK_LABELS, SUIT_NAMES, cardRank, cardSuit } from '@manille/core';

import { el } from './dom.js';

/**
 * Card faces are the CardForest "standard" deck (vectorised Bicycle-style
 * artwork by Chris Aguilar), vendored under `public/cards/`. Drawing pips by
 * hand never got closer than "vaguely card-shaped" — a real deck it is.
 *
 * File names use `T` for the ten and an uppercase suit letter: `card-TH.png`.
 */
const FILE_RANK: Record<string, string> = { '10': 'T' };

function imageFor(card: Card): string {
  const rank = cardRank(card);
  const suit = cardSuit(card).toUpperCase();
  return `${import.meta.env.BASE_URL}cards/card-${FILE_RANK[rank] ?? rank}${suit}.png`;
}

/**
 * A card is an image in a rounded frame — everything else (position, rotation,
 * the desaturation of an illegal card) is a CSS transform applied by whoever
 * owns the layout.
 */
export function cardElement(card: Card): HTMLElement {
  const node = el('div', 'card');
  node.dataset['card'] = card;
  node.dataset['suit'] = cardSuit(card);
  node.dataset['rank'] = cardRank(card);

  const img = el('img', 'card__face') as HTMLImageElement;
  img.src = imageFor(card);
  img.alt = `${RANK_LABELS[cardRank(card)]} ${SUIT_NAMES[cardSuit(card)]}`;
  // Every card in the deck is decoded during the deal animation otherwise.
  img.decoding = 'async';
  img.draggable = false;
  node.append(img);
  return node;
}

export function cardBack(): HTMLElement {
  return el('div', 'card-back');
}

/**
 * Pull the whole 32-card deck into cache — and, more to the point, into the
 * decoded-image cache — before anyone plays. A card first shown mid-trick was
 * flashing its white frame while the browser fetched and decoded the face;
 * `decoding = 'async'` avoids blocking the frame but does not avoid the gap.
 * The deck is under a megabyte all in, so there is nothing to be clever about.
 */
export function preloadCards(): void {
  for (const card of DECK) {
    const img = new Image();
    img.src = imageFor(card);
    // A card that fails to preload still works — it just loads the slow way.
    void img.decode().catch(() => {});
  }
}
